/**
 * Adaptive Retry Strategy with Backoff
 * 
 * Implements intelligent retry logic with:
 * - Exponential backoff with jitter
 * - Adaptive retry limits based on error types
 * - Request-specific retry policies
 * - Timeout management
 * - Retry budget tracking
 */

/**
 * Error classification for retry strategy
 */
export enum ErrorType {
  TRANSIENT = 'transient',       // Temporary, retry immediately
  RATE_LIMIT = 'rate_limit',     // Rate limited, backoff required
  TIMEOUT = 'timeout',           // Timeout, may retry with longer timeout
  VALIDATION = 'validation',     // Invalid input, don't retry
  AUTH = 'auth',                 // Auth failure, don't retry
  UNKNOWN = 'unknown'            // Unknown error, retry conservatively
}

/**
 * Retry configuration
 */
export interface RetryConfig {
  maxRetries: number;
  initialDelayMs: number;
  maxDelayMs: number;
  backoffMultiplier: number;
  timeoutMs: number;
  jitterFactor: number; // 0-1, adds randomness to backoff
}

/**
 * Retry context
 */
export interface RetryContext {
  attempt: number;
  maxAttempts: number;
  lastError: Error | null;
  totalElapsedMs: number;
  nextDelayMs: number;
}

/**
 * Retry result
 */
export interface RetryResult<T> {
  success: boolean;
  result?: T;
  error?: Error;
  attempts: number;
  totalDurationMs: number;
  retryHistory: Array<{
    attempt: number;
    error: string;
    delayMs: number;
  }>;
}

/**
 * Adaptive Retry Strategy
 */
export class AdaptiveRetryStrategy {
  private retryBudget = 100; // Total retries allowed per time window
  private budgetWindowMs = 60000; // 1 minute window
  private retryHistory: number[] = []; // Timestamps of retries

  constructor(
    private agentId: string,
    private config: RetryConfig = {
      maxRetries: 3,
      initialDelayMs: 1000,
      maxDelayMs: 30000,
      backoffMultiplier: 2,
      timeoutMs: 30000,
      jitterFactor: 0.2
    }
  ) {}

  /**
   * Execute with adaptive retry logic
   */
  async execute<T>(
    fn: () => Promise<T>,
    customConfig?: Partial<RetryConfig>
  ): Promise<RetryResult<T>> {
    const config = { ...this.config, ...customConfig };
    const startTime = Date.now();
    const retryHistory: RetryResult<T>['retryHistory'] = [];
    
    let lastError: Error | null = null;
    let attempt = 0;

    while (attempt < config.maxRetries) {
      attempt++;

      // Check retry budget
      if (!this.checkRetryBudget()) {
        console.warn(`⚠️  [${this.agentId}] Retry budget exhausted`);
        break;
      }

      try {
        console.log(`🔄 [${this.agentId}] Attempt ${attempt}/${config.maxRetries}`);
        
        // Execute with timeout
        const result = await this.executeWithTimeout(fn, config.timeoutMs);
        
        return {
          success: true,
          result,
          attempts: attempt,
          totalDurationMs: Date.now() - startTime,
          retryHistory
        };
      } catch (error) {
        lastError = error as Error;
        const errorType = this.classifyError(lastError);
        
        console.warn(`❌ [${this.agentId}] Attempt ${attempt} failed: ${lastError.message} (${errorType})`);

        // Don't retry non-retryable errors
        if (!this.shouldRetry(errorType, attempt, config.maxRetries)) {
          console.log(`🚫 [${this.agentId}] Error type ${errorType} is not retryable`);
          break;
        }

        // Calculate backoff delay
        const delay = this.calculateBackoff(attempt, errorType, config);
        
        retryHistory.push({
          attempt,
          error: lastError.message,
          delayMs: delay
        });

        // Wait before retry (unless last attempt)
        if (attempt < config.maxRetries) {
          console.log(`⏳ [${this.agentId}] Waiting ${delay}ms before retry...`);
          await this.sleep(delay);
        }

        // Track retry in budget
        this.retryHistory.push(Date.now());
      }
    }

    return {
      success: false,
      error: lastError || new Error('Max retries exceeded'),
      attempts: attempt,
      totalDurationMs: Date.now() - startTime,
      retryHistory
    };
  }

  /**
   * Execute function with timeout
   */
  private async executeWithTimeout<T>(
    fn: () => Promise<T>,
    timeoutMs: number
  ): Promise<T> {
    return Promise.race([
      fn(),
      new Promise<T>((_, reject) =>
        setTimeout(() => reject(new Error(`Timeout after ${timeoutMs}ms`)), timeoutMs)
      )
    ]);
  }

  /**
   * Classify error for retry strategy
   */
  private classifyError(error: Error): ErrorType {
    const message = error.message.toLowerCase();

    if (message.includes('rate limit') || message.includes('too many requests')) {
      return ErrorType.RATE_LIMIT;
    }
    if (message.includes('timeout') || message.includes('timed out')) {
      return ErrorType.TIMEOUT;
    }
    if (message.includes('validation') || message.includes('invalid')) {
      return ErrorType.VALIDATION;
    }
    if (message.includes('unauthorized') || message.includes('forbidden') || message.includes('auth')) {
      return ErrorType.AUTH;
    }
    if (message.includes('network') || message.includes('connection') || message.includes('econnrefused')) {
      return ErrorType.TRANSIENT;
    }

    return ErrorType.UNKNOWN;
  }

  /**
   * Determine if error should be retried
   */
  private shouldRetry(
    errorType: ErrorType,
    attempt: number,
    maxRetries: number
  ): boolean {
    // Never retry validation or auth errors
    if (errorType === ErrorType.VALIDATION || errorType === ErrorType.AUTH) {
      return false;
    }

    // Always retry transient errors
    if (errorType === ErrorType.TRANSIENT && attempt < maxRetries) {
      return true;
    }

    // Retry rate limits with longer backoff
    if (errorType === ErrorType.RATE_LIMIT && attempt < maxRetries) {
      return true;
    }

    // Retry timeouts
    if (errorType === ErrorType.TIMEOUT && attempt < maxRetries) {
      return true;
    }

    // Retry unknown errors conservatively
    if (errorType === ErrorType.UNKNOWN && attempt < maxRetries) {
      return true;
    }

    return false;
  }

  /**
   * Calculate backoff delay with jitter
   */
  private calculateBackoff(
    attempt: number,
    errorType: ErrorType,
    config: RetryConfig
  ): number {
    // Base exponential backoff
    let delay = Math.min(
      config.initialDelayMs * Math.pow(config.backoffMultiplier, attempt - 1),
      config.maxDelayMs
    );

    // Adjust for error type
    if (errorType === ErrorType.RATE_LIMIT) {
      // Longer backoff for rate limits
      delay *= 2;
    } else if (errorType === ErrorType.TRANSIENT) {
      // Shorter backoff for transient errors
      delay *= 0.5;
    }

    // Add jitter to prevent thundering herd
    const jitter = delay * config.jitterFactor * (Math.random() - 0.5) * 2;
    delay += jitter;

    return Math.max(100, Math.min(delay, config.maxDelayMs));
  }

  /**
   * Check if retry budget allows another attempt
   */
  private checkRetryBudget(): boolean {
    // Clean old retries outside window
    const cutoff = Date.now() - this.budgetWindowMs;
    this.retryHistory = this.retryHistory.filter(time => time > cutoff);

    return this.retryHistory.length < this.retryBudget;
  }

  /**
   * Get retry budget status
   */
  getRetryBudgetStatus(): {
    used: number;
    available: number;
    total: number;
    utilizationPercent: number;
  } {
    const cutoff = Date.now() - this.budgetWindowMs;
    const used = this.retryHistory.filter(time => time > cutoff).length;
    const available = this.retryBudget - used;

    return {
      used,
      available,
      total: this.retryBudget,
      utilizationPercent: (used / this.retryBudget) * 100
    };
  }

  /**
   * Sleep utility
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Reset retry history
   */
  reset(): void {
    this.retryHistory = [];
  }
}

/**
 * Retry Strategy Manager
 * Manages retry strategies for all agents
 */
export class RetryStrategyManager {
  private strategies = new Map<string, AdaptiveRetryStrategy>();

  /**
   * Get or create retry strategy for agent
   */
  getStrategy(
    agentId: string,
    config?: Partial<RetryConfig>
  ): AdaptiveRetryStrategy {
    if (!this.strategies.has(agentId)) {
      this.strategies.set(agentId, new AdaptiveRetryStrategy(agentId, {
        maxRetries: 3,
        initialDelayMs: 1000,
        maxDelayMs: 30000,
        backoffMultiplier: 2,
        timeoutMs: 30000,
        jitterFactor: 0.2,
        ...config
      }));
    }
    return this.strategies.get(agentId)!;
  }

  /**
   * Execute with retry protection
   */
  async executeWithRetry<T>(
    agentId: string,
    fn: () => Promise<T>,
    config?: Partial<RetryConfig>
  ): Promise<RetryResult<T>> {
    const strategy = this.getStrategy(agentId, config);
    return await strategy.execute(fn, config);
  }

  /**
   * Get retry budget status for all agents
   */
  getAllBudgetStatus(): Record<string, ReturnType<AdaptiveRetryStrategy['getRetryBudgetStatus']>> {
    const status: Record<string, any> = {};
    
    for (const [agentId, strategy] of this.strategies.entries()) {
      status[agentId] = strategy.getRetryBudgetStatus();
    }
    
    return status;
  }

  /**
   * Reset all retry strategies
   */
  resetAll(): void {
    for (const strategy of this.strategies.values()) {
      strategy.reset();
    }
  }
}

/**
 * Singleton instance
 */
export const retryStrategyManager = new RetryStrategyManager();
